import { Link } from "react-router-dom";
import SidePanel from "./SidePanel";
import { useStore } from "@/store/StoreContext";
import { useProducts } from "@/lib/siteContent";
import { formatBRL } from "@/lib/format";
import type { Product } from "@/types";

interface FavoritesPanelProps {
  open: boolean;
  onClose: () => void;
}

export default function FavoritesPanel({ open, onClose }: FavoritesPanelProps) {
  const { favorites, toggleFavorite } = useStore();
  const products = useProducts();
  const items = favorites
    .map((id) => products.find((product) => product.id === id))
    .filter((product): product is Product => Boolean(product));

  return (
    <SidePanel
      open={open}
      title="Seus favoritos"
      subtitle="Os produtos que você marcou com carinho."
      onClose={onClose}
    >
      {items.length === 0 ? (
        <div className="flex flex-col items-center py-14 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-100 text-accent-600">
            <i className="ri-heart-3-line text-3xl" />
          </span>
          <p className="mt-5 font-heading text-xl text-foreground-900">Nenhum favorito ainda</p>
          <p className="mt-2 max-w-xs text-sm text-foreground-600">
            Toque no coração dos produtos que você mais gostou e eles aparecerão aqui.
          </p>
          <Link
            to="/novidades"
            onClick={onClose}
            className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            Ver novidades
          </Link>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {items.map((product) => (
            <div
              key={product.id}
              className="flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-3"
            >
              <Link
                to={`/produto/${product.id}`}
                onClick={onClose}
                className="h-16 w-14 shrink-0 cursor-pointer overflow-hidden rounded-md bg-background-200"
              >
                <img
                  src={product.colors[0].image}
                  alt={product.name}
                  title={`${product.name} — Auréa`}
                  className="h-full w-full object-cover object-top"
                />
              </Link>
              <div className="min-w-0 flex-1">
                <Link
                  to={`/produto/${product.id}`}
                  onClick={onClose}
                  className="block cursor-pointer truncate font-heading text-lg text-foreground-900 transition-colors hover:text-primary-600"
                >
                  {product.name}
                </Link>
                <p className="text-xs text-foreground-500">
                  {formatBRL(product.promoPrice ?? product.price)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => toggleFavorite(product)}
                aria-label={`Remover ${product.name} dos favoritos`}
                className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full border border-background-300 text-foreground-600 transition-colors hover:bg-background-100"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
          ))}
        </div>
      )}
    </SidePanel>
  );
}